import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeSchoolClass } from '../constants/classes';

function attendanceDocId(date, className) {
  const d = String(date || '').trim();
  const c = normalizeSchoolClass(className) || String(className || '').trim();
  return `${d}__${c}`;
}

function normalizeStudentIds(presentStudents) {
  if (Array.isArray(presentStudents)) {
    return presentStudents.map((s) => String(s).trim()).filter(Boolean);
  }
  return String(presentStudents || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Upsert attendance for date + class (idempotent — re-save updates same record). */
export async function saveAttendance({ date, className, presentStudents, absentStudents }) {
  if (!db) throw new Error('Firestore not configured.');
  const present = normalizeStudentIds(presentStudents);
  const absent = normalizeStudentIds(absentStudents);
  const cls = normalizeSchoolClass(className) || className;
  const id = attendanceDocId(date, cls);
  await setDoc(
    doc(db, 'attendance', id),
    {
      date,
      class: cls,
      presentStudents: present,
      absentStudents: absent,
      totalStudents: present.length + absent.length,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
  return present.length;
}

export async function listAttendance() {
  if (!db) return [];
  const snap = await getDocs(collection(db, 'attendance'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Parse attendance doc id into { date, class } (supports date__class and date_class). */
function parseAttendanceDocId(docId, data) {
  if (data?.date && data?.class) {
    return { date: data.date, class: data.class };
  }
  const id = String(docId || '');
  const sep = id.includes('__') ? '__' : id.includes('_') ? '_' : null;
  if (!sep) return { date: '', class: '' };
  const idx = id.indexOf(sep);
  return { date: id.slice(0, idx), class: id.slice(idx + sep.length) };
}

/** School-wide attendance summary for a given date (YYYY-MM-DD). */
export async function getAttendanceSummaryForDate(date) {
  const targetDate = String(date || '').trim();
  if (!targetDate) return { date: '', classes: [], totals: { present: 0, absent: 0, marked: 0, unmarked: 0 } };

  const all = await listAttendance();
  const byClass = new Map();

  for (const row of all) {
    const parsed = parseAttendanceDocId(row.id, row);
    if (parsed.date !== targetDate) continue;
    const cls = parsed.class || row.class || '';
    if (!cls) continue;

    const present = Array.isArray(row.presentStudents) ? row.presentStudents : [];
    const absent = Array.isArray(row.absentStudents) ? row.absentStudents : [];
    const existing = byClass.get(cls) || { class: cls, presentStudents: [], absentStudents: [] };
    const presentSet = new Set([...existing.presentStudents, ...present.map(String)]);
    const absentSet = new Set([...existing.absentStudents, ...absent.map(String)]);
    for (const id of presentSet) absentSet.delete(id);
    byClass.set(cls, {
      class: cls,
      presentStudents: [...presentSet],
      absentStudents: [...absentSet],
      attendanceId: row.id,
    });
  }

  const classes = [...byClass.values()].sort((a, b) => String(a.class).localeCompare(String(b.class)));
  let present = 0;
  let absent = 0;
  for (const c of classes) {
    present += c.presentStudents.length;
    absent += c.absentStudents.length;
  }

  return {
    date: targetDate,
    classes,
    totals: {
      present,
      absent,
      marked: present + absent,
      classCount: classes.length,
    },
  };
}

/** Flat absent list with class for a date. */
export async function getAbsentStudentsForDate(date) {
  const summary = await getAttendanceSummaryForDate(date);
  const rows = [];
  for (const c of summary.classes) {
    for (const studentId of c.absentStudents) {
      rows.push({ studentId, class: c.class });
    }
  }
  return rows;
}

/** Load attendance for one class on a date (for edit). */
export async function getAttendanceForClassDate(date, className) {
  if (!db) return null;
  const cls = normalizeSchoolClass(className) || className;
  const id = attendanceDocId(date, cls);
  const snap = await getDoc(doc(db, 'attendance', id));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function deleteAttendance(attendanceId) {
  if (!db) throw new Error('Firestore not configured.');
  const id = String(attendanceId || '').trim();
  if (!id) throw new Error('Attendance record id is required.');
  await deleteDoc(doc(db, 'attendance', id));
}
